/**
 * Service for managing microphone permissions.
 * Wraps expo-av permission APIs with typed results.
 */

import { Audio } from 'expo-av';

export type PermissionStatus = 'granted' | 'denied' | 'undetermined';

export class AudioPermissionService {
  private static instance: AudioPermissionService;

  static getInstance(): AudioPermissionService {
    if (!AudioPermissionService.instance) {
      AudioPermissionService.instance = new AudioPermissionService();
    }
    return AudioPermissionService.instance;
  }

  async checkPermission(): Promise<PermissionStatus> {
    const { status } = await Audio.getPermissionsAsync();
    return this.mapStatus(status);
  }

  async requestPermission(): Promise<PermissionStatus> {
    const { status } = await Audio.requestPermissionsAsync();
    return this.mapStatus(status);
  }

  async ensurePermission(): Promise<boolean> {
    const current = await this.checkPermission();
    if (current === 'granted') return true;

    const requested = await this.requestPermission();
    return requested === 'granted';
  }

  private mapStatus(status: string): PermissionStatus {
    if (status === 'granted') return 'granted';
    if (status === 'denied') return 'denied';
    return 'undetermined';
  }
}

export const audioPermissionService = AudioPermissionService.getInstance();
